import React, { useState } from 'react';

const GroupForm = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    destination: '',
    start_date: '',
    end_date: '',
    component_type: 'cruise',
    max_participants: '',
    packages: [
      {
        id: 1,
        name: '',
        description: '',
        price: '',
        inclusions: []
      }
    ],
    payment_schedule: [
      {
        id: 1,
        name: 'Deposit',
        percentage: 30,
        due_date: ''
      }
    ],
    coordinator: {
      name: '',
      email: '',
      phone: ''
    },
    cancellation_policy: ''
  });

  const [currentInclusion, setCurrentInclusion] = useState('');

  const componentTypes = [
    { value: 'cruise', label: 'Cruise' },
    { value: 'tour', label: 'Tour/Activity' },
    { value: 'hotel', label: 'Hotel Package' },
    { value: 'flight', label: 'Flight Package' },
    { value: 'all-inclusive', label: 'All-Inclusive Resort' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.destination || !formData.start_date || !formData.end_date) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate at least one package
    if (!formData.packages[0].name || !formData.packages[0].price) {
      alert('Please add at least one package with name and price');
      return;
    }

    onSubmit(formData);
  };

  const handlePackageChange = (index, field, value) => {
    const updatedPackages = [...formData.packages];
    updatedPackages[index][field] = value;
    setFormData({ ...formData, packages: updatedPackages });
  };

  const addPackage = () => {
    setFormData({
      ...formData,
      packages: [
        ...formData.packages,
        {
          id: formData.packages.length + 1,
          name: '',
          description: '',
          price: '',
          inclusions: []
        }
      ]
    });
  };

  const removePackage = (index) => {
    if (formData.packages.length > 1) {
      const updatedPackages = formData.packages.filter((_, i) => i !== index);
      setFormData({ ...formData, packages: updatedPackages });
    }
  };

  const addInclusion = (packageIndex) => {
    if (currentInclusion.trim()) {
      const updatedPackages = [...formData.packages];
      updatedPackages[packageIndex].inclusions.push(currentInclusion.trim());
      setFormData({ ...formData, packages: updatedPackages });
      setCurrentInclusion('');
    }
  };

  const removeInclusion = (packageIndex, inclusionIndex) => {
    const updatedPackages = [...formData.packages];
    updatedPackages[packageIndex].inclusions.splice(inclusionIndex, 1);
    setFormData({ ...formData, packages: updatedPackages });
  };

  const handlePaymentScheduleChange = (index, field, value) => {
    const updatedSchedule = [...formData.payment_schedule];
    updatedSchedule[index][field] = value;
    setFormData({ ...formData, payment_schedule: updatedSchedule });
  };

  const addPaymentSchedule = () => {
    setFormData({
      ...formData,
      payment_schedule: [
        ...formData.payment_schedule,
        {
          id: formData.payment_schedule.length + 1,
          name: '',
          percentage: 0,
          due_date: ''
        }
      ]
    });
  };

  const removePaymentSchedule = (index) => {
    if (formData.payment_schedule.length > 1) {
      const updatedSchedule = formData.payment_schedule.filter((_, i) => i !== index);
      setFormData({ ...formData, payment_schedule: updatedSchedule });
    }
  };

  return (
    <div className="group-form-container">
      <div className="group-modal-header">
        <h2>Create Group Booking</h2>
        <button onClick={onClose} className="close-modal-btn">×</button>
      </div>

      <form onSubmit={handleSubmit} className="group-modal-content">
        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Group Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Caribbean Cruise Adventure 2025"
                required
              />
            </div>

            <div className="form-group">
              <label>Destination *</label>
              <input
                type="text"
                value={formData.destination}
                onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                placeholder="e.g., Eastern Caribbean"
                required
              />
            </div>

            <div className="form-group">
              <label>Component Type *</label>
              <select
                value={formData.component_type}
                onChange={(e) => setFormData({ ...formData, component_type: e.target.value })}
                required
              >
                {componentTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Max Participants</label>
              <input
                type="number"
                value={formData.max_participants}
                onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                placeholder="Leave blank for unlimited"
                min="1"
              />
            </div>

            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>End Date *</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                required
              />
            </div>
          </div>
        </div>

        {/* Packages */}
        <div className="form-section">
          <div className="section-header">
            <h3>Packages</h3>
            <button type="button" onClick={addPackage} className="add-btn">
              + Add Package
            </button>
          </div>
          
          {formData.packages.map((pkg, index) => (
            <div key={pkg.id} className="package-card">
              <div className="package-header">
                <h4>Package {index + 1}</h4>
                {formData.packages.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePackage(index)}
                    className="remove-btn"
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Package Name *</label>
                  <input
                    type="text"
                    value={pkg.name}
                    onChange={(e) => handlePackageChange(index, 'name', e.target.value)}
                    placeholder="e.g., Balcony Stateroom"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Price per Person *</label>
                  <input
                    type="number"
                    value={pkg.price}
                    onChange={(e) => handlePackageChange(index, 'price', e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={pkg.description}
                    onChange={(e) => handlePackageChange(index, 'description', e.target.value)}
                    placeholder="Package details and features..."
                    rows="3"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Inclusions</label>
                  <div className="inclusions-input">
                    <input
                      type="text"
                      value={currentInclusion}
                      onChange={(e) => setCurrentInclusion(e.target.value)}
                      placeholder="Add inclusion..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addInclusion(index);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => addInclusion(index)}
                      className="add-inclusion-btn"
                    >
                      Add
                    </button>
                  </div>
                  <div className="inclusions-list">
                    {pkg.inclusions.map((inclusion, i) => (
                      <span key={i} className="inclusion-tag">
                        {inclusion}
                        <button
                          type="button"
                          onClick={() => removeInclusion(index, i)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Payment Schedule */}
        <div className="form-section">
          <div className="section-header">
            <h3>Payment Schedule</h3>
            <button type="button" onClick={addPaymentSchedule} className="add-btn">
              + Add Payment
            </button>
          </div>
          
          {formData.payment_schedule.map((payment, index) => (
            <div key={payment.id} className="payment-schedule-item">
              <div className="form-grid">
                <div className="form-group">
                  <label>Payment Name</label>
                  <input
                    type="text"
                    value={payment.name}
                    onChange={(e) => handlePaymentScheduleChange(index, 'name', e.target.value)}
                    placeholder="e.g., Deposit, Final Payment"
                  />
                </div>

                <div className="form-group">
                  <label>Percentage</label>
                  <input
                    type="number"
                    value={payment.percentage}
                    onChange={(e) => handlePaymentScheduleChange(index, 'percentage', e.target.value)}
                    placeholder="30"
                    min="0"
                    max="100"
                  />
                </div>

                <div className="form-group">
                  <label>Due Date</label>
                  <input
                    type="date"
                    value={payment.due_date}
                    onChange={(e) => handlePaymentScheduleChange(index, 'due_date', e.target.value)}
                  />
                </div>

                {formData.payment_schedule.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePaymentSchedule(index)}
                    className="remove-btn"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Coordinator Information */}
        <div className="form-section">
          <h3>Group Coordinator</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Coordinator Name</label>
              <input
                type="text"
                value={formData.coordinator.name}
                onChange={(e) => setFormData({
                  ...formData,
                  coordinator: { ...formData.coordinator, name: e.target.value }
                })}
                placeholder="Group leader name"
              />
            </div>

            <div className="form-group">
              <label>Coordinator Email</label>
              <input
                type="email"
                value={formData.coordinator.email}
                onChange={(e) => setFormData({
                  ...formData,
                  coordinator: { ...formData.coordinator, email: e.target.value }
                })}
                placeholder="coordinator@email.com"
              />
            </div>

            <div className="form-group">
              <label>Coordinator Phone</label>
              <input
                type="tel"
                value={formData.coordinator.phone}
                onChange={(e) => setFormData({
                  ...formData,
                  coordinator: { ...formData.coordinator, phone: e.target.value }
                })}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
        </div>

        {/* Cancellation Policy */}
        <div className="form-section">
          <h3>Cancellation Policy</h3>
          <div className="form-group full-width">
            <textarea
              value={formData.cancellation_policy}
              onChange={(e) => setFormData({ ...formData, cancellation_policy: e.target.value })}
              placeholder="Enter cancellation policy details..."
              rows="5"
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Create Group Booking
          </button>
        </div>
      </form>
    </div>
  );
};

export default GroupForm;
